import { ErrorStatus, isHttpError } from '@getstep/sdk/dist/http/HttpError'
import { ErrorMessage } from '@hookform/error-message'
import debounce from 'lodash/debounce'
import { observer } from 'mobx-react'
import { useRouter } from 'next/router'
import type { FunctionComponent, MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import type { UseFormSetError } from 'react-hook-form'
import { useController, useForm } from 'react-hook-form'

import { Button } from '../../../components/button/button'
import { TextInput } from '../../../components/input/text-input'
import { CustomFieldId, useField } from '../../../graphql/hooks/field'
import type { CustomFieldsetId } from '../../../graphql/hooks/form-page'
import { useDevMenuItem } from '../../../lib/hooks/useDevMenuItem'
import { useDidMount } from '../../../lib/hooks/useDidMount'
import type { EmailVerificationStore } from '../../../lib/store/session'
import { PhoneVerificationStore } from '../../../lib/store/session'
import { DevActionGroup, DevActionName } from '../../../lib/store/ui/dev-menu'
import { TEXT } from '../../../lib/utils/text'
import {
    inviteAdult,
    WHITELISTED_VERIFY_CODE,
} from '../../../shared/util/tests'
import { Legend } from '../legend'
import type { FormProps } from '../props'
import styles from './enter-code.module.scss'

const OTP_LENGTH = 6

/**
 * This form verifies OTP for given phone number/email.
 */
export const EnterCode: FunctionComponent<React.PropsWithChildren<Props>> =
    observer(({ store, onSubmit, renderSubmit, title, customFieldsetId }) => {
        const mounted = useDidMount()
        const isPhone = store instanceof PhoneVerificationStore
        const { valueToVerify = '', startVerification, canResendCode } = store
        const {
            query: { code },
        } = useRouter()
        const [resending, setResending] = useState(false)
        const fieldId = isPhone
            ? CustomFieldId.LoginOtp
            : CustomFieldId.LoginEmailOtp
        const { errorTexts, placeholder } = useField(fieldId)

        const {
            reset,
            handleSubmit,
            setError,
            control,
            watch,
            setValue,
            formState: { submitCount, errors },
        } = useForm<Values>({
            defaultValues: {
                otp: typeof code === 'string' && code ? code : '',
            },
        })

        const {
            field,
            fieldState: { invalid },
        } = useController({
            control,
            name: 'otp',
            rules: {
                required: {
                    value: true,
                    message: errorTexts.required,
                },
                minLength: {
                    value: OTP_LENGTH,
                    message: errorTexts.minLength,
                },
                maxLength: {
                    value: OTP_LENGTH,
                    message: errorTexts.maxLength,
                },
            },
        })

        const otp = watch('otp')

        const submit = handleSubmit(async ({ otp }) =>
            onSubmit(otp, { setError }),
        )

        useEffect(
            debounce(() => {
                if (otp.length === OTP_LENGTH) submit()
            }, 100),
            [otp],
        )

        useDevMenuItem({
            group: DevActionGroup.PAGE,
            name: DevActionName.FILL_OTP,
            message: 'Filled OTP with whitelisted code',
            onActivate() {
                setValue('otp', WHITELISTED_VERIFY_CODE)
            },
        })

        useDevMenuItem({
            group: DevActionGroup.USER,
            name: DevActionName.GENERATE_INVITE,
            message: 'Sent you an invite from a friendly wolf 😉',
            async onActivate() {
                await inviteAdult({ phoneNumber: valueToVerify })
            },
        })

        return (
            <form onSubmit={submit} className={styles.form}>
                {mounted && (
                    <Legend
                        preset='primary'
                        customFieldsetId={customFieldsetId}
                    >
                        {title}
                    </Legend>
                )}

                <TextInput
                    {...field}
                    className={styles.input}
                    autoFocus
                    preset='material'
                    autoComplete='one-time-code'
                    placeholder={placeholder}
                    size='medium'
                    validationCount={submitCount}
                    valid={!invalid}
                    maxLength={OTP_LENGTH}
                    inputMode='numeric'
                    data-testid='login.code.input'
                    error={<ErrorMessage errors={errors} name='otp' />}
                />

                {canResendCode && (
                    <Button
                        animatePress={false}
                        onClick={async (e: MouseEvent) => {
                            e.preventDefault()

                            reset()
                            setResending(true)

                            try {
                                await startVerification(valueToVerify)
                            } catch (e) {
                                if (!(e instanceof Error)) throw e

                                if (
                                    isHttpError(e, {
                                        status: ErrorStatus.RESOURCE_EXHAUSTED,
                                    })
                                ) {
                                    setError('otp', {
                                        message: errorTexts.resourceExhausted,
                                    })
                                    return
                                }
                                throw e
                            } finally {
                                setResending(false)
                            }
                        }}
                        preset='anchor'
                        size='small'
                        className={styles.resend}
                        loading={resending}
                        data-testid='login.code.resend'
                    >
                        {TEXT.resendCode}
                    </Button>
                )}

                {renderSubmit(control)}
            </form>
        )
    })

interface SubmitMethods {
    setError: UseFormSetError<Values>
}

interface Props extends FormProps<Values> {
    customFieldsetId: CustomFieldsetId
    store: PhoneVerificationStore | EmailVerificationStore
    onSubmit: (otp: string, methods: SubmitMethods) => void
}

export interface Values {
    otp: string
}
